import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Zap, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ReadinessDots } from "./CardReadiness";

/** DSP-1 — the dispatch pool grouped by Crew · Scope · Skills, quick requests on top, vehicles under Warehouse,
 *  a review panel per card and stamped notes. Read-only users see disabled controls with a tooltip. */
const RO = "Dispatch changes need the Scheduler permission";
const stamp = (n) => `${n.initials} ${new Date(n.at).toLocaleDateString("en-US", { month: "numeric", day: "numeric" })}`;

function ReviewPanel({ card, canWrite, onChanged }) {
  const [r, setR] = useState(null);
  useEffect(() => { api.get(`/dispatch/review/${card.id}`).then((x) => setR(x.data)).catch(() => setR({ error: true })); }, [card.id]);
  if (!r) return <div className="text-[11px] text-muted-foreground">review…</div>;
  if (r.error) return <div className="text-[11px] text-red-600">Could not load the review</div>;
  const Row = ({ label, it, testId }) => (
    <div className="flex items-start gap-2 text-[11px]" data-testid={testId} data-colour={it.colour}>
      <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${{ green: "bg-emerald-500", yellow: "bg-amber-400", red: "bg-red-500" }[it.colour] || "bg-muted-foreground/40"}`} />
      <span className="w-20 shrink-0 text-muted-foreground">{label}</span><span className="flex-1 min-w-0">{it.reason}</span>
    </div>
  );
  const dispatch = async () => {
    try { await api.post("/mobilizations/bulk-status", { ids: [card.id], status: "dispatched", confirm_unsigned_co: true }); toast.success("Dispatched"); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail?.detail || e.response?.data?.detail || "Dispatch failed"); }
  };
  return (
    <div className="mt-2 rounded border border-border bg-muted/30 p-2 space-y-1" data-testid={`review-panel-${card.id}`}>
      <Row label="Material" it={r.readiness.material} testId="review-material" />
      <Row label="Submittals" it={r.readiness.submittals} testId="review-submittals" />
      <Row label="GC Ready" it={r.readiness.gc_ready} testId="review-gc-ready" />
      <Row label="CO" it={r.readiness.co} testId="review-co" />
      <Row label="Delivery" it={r.delivery} testId="review-delivery" />
      <Row label="Crew" it={r.crew} testId="review-crew" />
      <Row label="Skills" it={r.skills} testId="review-skills" />
      {card.status === "ready_to_dispatch" && (
        <button type="button" disabled={!canWrite} title={canWrite ? "Dispatch this card" : RO} onClick={dispatch} data-testid={`review-dispatch-${card.id}`}
                className="mt-1 text-[11px] rounded border border-border bg-foreground text-background px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed">Dispatch</button>
      )}
    </div>
  );
}

function Card({ card, canWrite, onChanged }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/mobilizations/${card.id}/notes`, { text: note }); setNote(""); onChanged?.(); } catch { toast.error("Note failed"); }
  };
  return (
    <div className={`rounded border bg-background p-2 ${card.conflicts?.length ? "border-red-500" : "border-border"}`} data-testid={`pool-card-${card.id}`} data-status={card.status}>
      <div className="flex items-center gap-2 text-xs">
        {card.is_quick_request && <Zap className="w-3 h-3 text-amber-600" title="Quick request" />}
        {card.priority && <span className="text-[9px] font-semibold uppercase rounded bg-foreground text-background px-1">Priority</span>}
        <span className="font-medium text-foreground truncate">{card.job_number ? `${card.job_number} · ` : ""}{card.project_name}</span>
        <span className="font-semibold">{card.scope}</span>
        {card.gc_rating && <span className="text-[9px] rounded border border-border px-1">GC {card.gc_rating}</span>}
        <span className="ml-auto text-muted-foreground">{card.scheduled_start_date || "—"}{card.scheduled_end_date && card.scheduled_end_date !== card.scheduled_start_date ? ` → ${card.scheduled_end_date}` : ""}</span>
      </div>
      <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
        <ReadinessDots r={Object.fromEntries(Object.entries(card.dots).map(([k, c]) => [k, { colour: c, reason: card.reasons[k] }]))} testId={`pool-dots-${card.id}`} />
        <span>{card.foreman}{card.crew?.length ? ` · ${card.crew.length} crew` : ""}</span>
        {card.hours > 0 && <span>· {card.hours} h{card.days ? ` · ${card.days} d` : ""}</span>}
        {card.conflicts?.length > 0 && <span className="text-red-600 font-semibold" data-testid={`pool-conflict-${card.id}`} title={card.conflicts.map((c) => `${c.installer} also on ${c.other_title} ${c.date}`).join("\n")}>conflict · {card.conflicts[0].installer} on {card.conflicts[0].other_title}</span>}
        <button type="button" onClick={() => setOpen((o) => !o)} className="ml-auto underline" data-testid={`pool-review-toggle-${card.id}`}>{open ? "hide review" : "review"}</button>
      </div>
      {open && <ReviewPanel card={card} canWrite={canWrite} onChanged={onChanged} />}
      {(card.card_notes || []).length > 0 && (
        <div className="mt-1.5 space-y-0.5" data-testid={`pool-notes-${card.id}`}>
          {card.card_notes.slice(-3).map((n) => <div key={n.id} className="text-[11px] text-foreground/90"><span className="font-mono text-muted-foreground mr-1" title={`${n.user} · ${new Date(n.at).toLocaleString()}`}>{stamp(n)}</span>{n.text}</div>)}
        </div>
      )}
      <div className="mt-1.5 flex items-center gap-1">
        <MessageSquare className="w-3 h-3 text-muted-foreground" />
        <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder="Add a note (stamped, never edited)"
               data-testid={`pool-note-input-${card.id}`} className="flex-1 h-6 text-[11px] bg-transparent border-b border-border focus:outline-none" />
      </div>
    </div>
  );
}

function Group({ k, title, count, expanded, onToggleGroup, children }) {
  const open = expanded[k] !== false;
  return (
    <section className="rounded border border-border" data-testid={`pool-group-${k}`}>
      <button type="button" onClick={() => onToggleGroup(k, !open)} className="w-full flex items-center gap-2 px-2 py-1.5 text-xs font-semibold text-foreground bg-muted/40" data-testid={`pool-group-toggle-${k}`}>
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}{title}<span className="text-muted-foreground font-normal">· {count}</span>
      </button>
      {open && <div className="p-2 space-y-2">{children}</div>}
    </section>
  );
}

export default function DispatchPoolPanel({ grouping, expanded, onToggleGroup, onChanged, reloadKey }) {
  const [data, setData] = useState(null);
  const load = async () => { try { const { data } = await api.get("/dispatch/pool", { params: { grouping } }); setData(data); } catch { setData({ groups: [], vehicles: [], quick_requests: [] }); } };
  useEffect(() => { load(); }, [grouping, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // Only takes space when there is something to act on; the calendar is the dispatch view.
  if (!data || (!data.quick_requests.length && !data.groups.length)) return null;
  const canWrite = !!data.can_write;
  const changed = () => { load(); onChanged?.(); };

  const decide = async (q, approve) => {
    try {
      if (approve) {
        const { data: s } = await api.get(`/dispatch/quick-requests/${q.id}/suggestions`);
        const names = [...s.nearby, ...s.free].slice(0, 8).map((p, i) => `${i + 1}. ${p.name} — ${p.why}`).join("\n");
        const pick = window.prompt(`Approve "${q.what}" — pick installer numbers (comma separated):\n${names}`);
        if (!pick) return;
        const all = [...s.nearby, ...s.free];
        const ids = pick.split(",").map((x) => all[parseInt(x, 10) - 1]?.id).filter(Boolean);
        const { data: r } = await api.post(`/dispatch/quick-requests/${q.id}/approve`, { installer_ids: ids });
        toast.success(`Dispatched${r.ticket_id ? " · pull ticket drafted" : ""}`);
      } else {
        const reason = window.prompt("Decline — reason (goes back to the foreman)");
        if (!reason) return;
        await api.post(`/dispatch/quick-requests/${q.id}/decline`, { reason });
      }
      changed();
    } catch (e) { toast.error(e.response?.data?.detail?.detail || e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="w-full space-y-2 xl:w-[380px]" data-testid="dispatch-pool" id="dispatch-pool">
      {data.quick_requests.length > 0 && (
        <Group k="quick" title="Quick requests" count={data.quick_requests.length} expanded={expanded} onToggleGroup={onToggleGroup}>
          {data.quick_requests.map((q) => (
            <div key={q.id} className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2 text-xs" data-testid={`quick-request-${q.id}`}>
              <div className="flex items-center gap-2"><Zap className="w-3 h-3 text-amber-600" /><span className="font-semibold">Quick request</span><span className="text-muted-foreground">· {q.foreman} · {q.project_name}{q.scope ? ` · ${q.scope}` : ""}</span><span className="ml-auto">{q.estimated_hours} h · by {q.needed_by || "today"}</span></div>
              <div className="mt-1 text-foreground/90">{q.what}</div>
              <div className="mt-1.5 flex gap-1">
                <button type="button" disabled={!canWrite} title={canWrite ? "Approve — pick installers" : RO} onClick={() => decide(q, true)} data-testid={`quick-approve-${q.id}`} className="rounded border border-border bg-foreground text-background px-2 py-0.5 disabled:opacity-40 disabled:cursor-not-allowed">Approve</button>
                <button type="button" disabled={!canWrite} title={canWrite ? "Decline with a reason" : RO} onClick={() => decide(q, false)} data-testid={`quick-decline-${q.id}`} className="rounded border border-border px-2 py-0.5 disabled:opacity-40 disabled:cursor-not-allowed">Decline</button>
              </div>
            </div>
          ))}
        </Group>
      )}
      {data.groups.map((g) => <Group key={g.key} k={g.key} title={g.key} count={g.cards.length} expanded={expanded} onToggleGroup={onToggleGroup}>{g.cards.map((c) => <Card key={`${g.key}-${c.id}`} card={c} canWrite={canWrite} onChanged={changed} />)}</Group>)}
    </div>
  );
}
